import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  IconButton,
  Skeleton,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Drawer,
  Divider,
} from '@mui/material';
import {
  Schedule,
  Timeline,
  Inventory,
  Assignment,
  TrendingUp,
  Add,
  PlayArrow,
  CheckCircle,
  Refresh,
  CalendarViewWeek,
  CalendarViewMonth,
  Visibility,
  Close,
  AutoFixHigh,
} from '@mui/icons-material';

// Strip /api/v1 suffix so apiFetch paths (which start with /api/v1/...) aren't double-prefixed
const PLANNING_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1'
).replace(/\/api\/v\d+$/, '');

// ─── Types ────────────────────────────────────────────────────────────────────
interface MpsRecord {
  id?: string | number;
  itemCode?: string;
  item?: string;
  period?: string;
  plannedQty?: number;
  actualQty?: number;
  status?: string;
  [key: string]: any;
}

interface PlannedOrder {
  id?: string | number;
  itemCode?: string;
  requiredQty?: number;
  availableQty?: number;
  shortage?: number;
  plannedOrderDate?: string;
  status?: string;
  [key: string]: any;
}

interface CrpRow {
  id?: string | number;
  workCenter?: string;
  capacityAvailable?: number;
  capacityRequired?: number;
  utilization?: number;
  [key: string]: any;
}

interface ProductionSchedule {
  id?: string | number;
  workOrderId?: string | number;
  workOrder?: string;
  item?: string;
  itemCode?: string;
  workCenter?: string;
  workCenterId?: string | number;
  scheduledStart?: string;
  scheduledEnd?: string;
  status?: string;
  [key: string]: any;
}

interface MpsConflict {
  type?: string;
  workCenter?: string;
  date?: string;
  severity?: string;
  description?: string;
  [key: string]: any;
}

interface PlannedOrderDetail {
  id?: string | number;
  itemCode?: string;
  item?: string;
  quantity?: number;
  plannedStart?: string;
  plannedEnd?: string;
  status?: string;
  source?: string;
  materialRequirements?: Array<{
    material?: string;
    requiredQty?: number;
    availableQty?: number;
    shortage?: number;
    unit?: string;
    [key: string]: any;
  }>;
  [key: string]: any;
}

interface CrpUtilizationDetail {
  week?: string;
  plannedHours?: number;
  availableHours?: number;
  utilizationPercent?: number;
  date?: string;
  [key: string]: any;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
async function apiFetch(path: string, options: RequestInit = {}) {
  const token = localStorage.getItem('token');
  const resp = await fetch(`${PLANNING_BASE_URL}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...options,
  });
  if (!resp.ok) throw { message: `HTTP ${resp.status}` };
  return resp.json();
}

const STATUS_COLORS: Record<
  string,
  'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'
> = {
  DRAFT: 'default',
  ACTIVE: 'primary',
  APPROVED: 'success',
  COMPLETED: 'success',
  CANCELLED: 'error',
  IN_PROGRESS: 'warning',
  PENDING: 'warning',
};

const GANTT_COLORS: Record<string, string> = {
  DRAFT: '#9e9e9e',
  ACTIVE: '#1976d2',
  COMPLETED: '#388e3c',
  CANCELLED: '#d32f2f',
  IN_PROGRESS: '#f57c00',
  PENDING: '#ffa000',
};

const StatusChip: React.FC<{ status?: string }> = ({ status }) => {
  const s = (status ?? 'UNKNOWN').toUpperCase();
  const color = STATUS_COLORS[s] ?? 'default';
  return <Chip label={s} color={color} size="small" />;
};

// ─── Utilization bar ──────────────────────────────────────────────────────────
const UtilizationBar: React.FC<{ value: number }> = ({ value }) => {
  const color =
    value > 95 ? 'error' : value > 80 ? 'warning' : 'success';
  return (
    <Box display="flex" alignItems="center" gap={1}>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, value)}
        color={color}
        sx={{ width: 80, height: 8, borderRadius: 4 }}
      />
      <Typography variant="body2" color={`${color}.main`}>
        {value.toFixed(1)}%
      </Typography>
    </Box>
  );
};

// ─── MPS Conflict Severity chip ───────────────────────────────────────────────
const SeverityChip: React.FC<{ severity?: string }> = ({ severity }) => {
  const s = (severity ?? '').toLowerCase();
  const color =
    s === 'critical' || s === 'high'
      ? 'error'
      : s === 'medium'
      ? 'warning'
      : 'default';
  const label =
    s === 'critical' || s === 'high'
      ? 'HIGH'
      : s === 'medium'
      ? 'MEDIUM'
      : 'LOW';
  return <Chip label={label} color={color as any} size="small" />;
};

// ─── Tab 1: MPS ───────────────────────────────────────────────────────────────
const MpsTab: React.FC = () => {
  const [rows, setRows] = useState<MpsRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [form, setForm] = useState({
    itemCode: '',
    period: '',
    plannedQty: '',
    status: 'DRAFT',
  });

  // Conflict detection state
  const [selectedMpsId, setSelectedMpsId] = useState<string | number | null>(null);
  const [conflicts, setConflicts] = useState<MpsConflict[]>([]);
  const [conflictsLoading, setConflictsLoading] = useState(false);
  const [optimizing, setOptimizing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/api/v1/planning/mps');
      const list = data.data ?? data;
      setRows(Array.isArray(list) ? list : []);
    } catch (e: any) {
      setError(e.message || 'Failed to load MPS data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const fetchConflicts = useCallback(async (mpsId: string | number) => {
    setConflictsLoading(true);
    try {
      const data = await apiFetch(`/api/v1/planning/mps/${mpsId}/conflicts`);
      const list = data.data ?? data;
      setConflicts(Array.isArray(list) ? list : []);
    } catch (e: any) {
      setSnack({ open: true, message: `Failed to fetch conflicts: ${e.message}`, severity: 'error' });
      setConflicts([]);
    } finally {
      setConflictsLoading(false);
    }
  }, []);

  const handleSelectRow = (id: string | number) => {
    if (selectedMpsId === id) {
      setSelectedMpsId(null);
      setConflicts([]);
    } else {
      setSelectedMpsId(id);
      fetchConflicts(id);
    }
  };

  const handleOptimize = async (mpsId: string | number) => {
    setOptimizing(true);
    try {
      await apiFetch(`/api/v1/planning/mps/${mpsId}/optimize`, { method: 'PUT' });
      setSnack({ open: true, message: 'Optimization complete', severity: 'success' });
      fetchConflicts(mpsId);
    } catch (e: any) {
      setSnack({ open: true, message: `Optimize failed: ${e.message}`, severity: 'error' });
    } finally {
      setOptimizing(false);
    }
  };

  const handleCreate = async () => {
    try {
      await apiFetch('/api/v1/planning/mps', {
        method: 'POST',
        body: JSON.stringify({
          itemCode: form.itemCode,
          period: form.period,
          plannedQty: Number(form.plannedQty),
          status: form.status,
        }),
      });
      setSnack({ open: true, message: 'MPS record created', severity: 'success' });
      setCreateOpen(false);
      setForm({ itemCode: '', period: '', plannedQty: '', status: 'DRAFT' });
      load();
    } catch (e: any) {
      setSnack({ open: true, message: `Create failed: ${e.message}`, severity: 'error' });
    }
  };

  const handleAction = async (id: string | number, action: string) => {
    setActionLoading(`${id}-${action}`);
    try {
      const options: RequestInit = { method: action === 'approve' ? 'PUT' : 'PUT' };
      if (action === 'approve') {
        const token = localStorage.getItem('token');
        const payload = JSON.parse(atob(token!.split('.')[1]));
        const userId = payload.sub || payload.userId || payload.id;
        options.body = JSON.stringify({ approvedBy: userId });
      }
      await apiFetch(`/api/v1/planning/mps/${id}/${action}`, options);
      setSnack({ open: true, message: `Action "${action}" successful`, severity: 'success' });
      load();
    } catch (e: any) {
      setSnack({ open: true, message: `Action failed: ${e.message}`, severity: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Master Production Schedule</Typography>
        <Box display="flex" gap={1}>
          <Button size="small" startIcon={<Refresh />} onClick={load}>
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateOpen(true)}
          >
            Create MPS
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item Code</TableCell>
              <TableCell>Period</TableCell>
              <TableCell align="right">Planned Qty</TableCell>
              <TableCell align="right">Actual Qty</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 6 }).map((__, c) => (
                    <TableCell key={c}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary" py={2}>
                    No MPS records found. Create one to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const id = row.id ?? Math.random();
                const status = row.status ?? 'DRAFT';
                const isSelected = selectedMpsId === id;
                return (
                  <React.Fragment key={String(id)}>
                    <TableRow
                      hover
                      selected={isSelected}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleSelectRow(id)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {row.itemCode ?? row.item ?? '--'}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.period ?? '--'}</TableCell>
                      <TableCell align="right">{row.plannedQty ?? '--'}</TableCell>
                      <TableCell align="right">{row.actualQty ?? '--'}</TableCell>
                      <TableCell>
                        <StatusChip status={status} />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5} onClick={(e) => e.stopPropagation()}>
                          {status === 'DRAFT' && (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={actionLoading === `${id}-approve`}
                              onClick={() => handleAction(id, 'approve')}
                            >
                              Approve
                            </Button>
                          )}
                          {status === 'APPROVED' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              disabled={actionLoading === `${id}-activate`}
                              onClick={() => handleAction(id, 'activate')}
                            >
                              Activate
                            </Button>
                          )}
                          {status === 'ACTIVE' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              disabled={actionLoading === `${id}-complete`}
                              onClick={() => handleAction(id, 'complete')}
                            >
                              Complete
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={optimizing && isSelected ? <CircularProgress size={12} color="inherit" /> : <AutoFixHigh />}
                            disabled={optimizing && isSelected}
                            onClick={() => {
                              setSelectedMpsId(id);
                              handleOptimize(id);
                            }}
                          >
                            Optimize
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Inline Conflicts Panel */}
                    {isSelected && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ p: 0, bgcolor: 'grey.50' }}>
                          <Box p={2}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                Conflicts for {row.itemCode ?? row.item ?? String(id)}
                              </Typography>
                              {conflictsLoading && <CircularProgress size={16} />}
                            </Box>
                            {conflictsLoading ? (
                              <Skeleton variant="rectangular" height={40} />
                            ) : conflicts.length === 0 ? (
                              <Chip
                                label="No conflicts detected"
                                color="success"
                                size="small"
                                icon={<CheckCircle />}
                              />
                            ) : (
                              <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Severity</TableCell>
                                      <TableCell>Type</TableCell>
                                      <TableCell>Work Center</TableCell>
                                      <TableCell>Date</TableCell>
                                      <TableCell>Description</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {conflicts.map((c, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell>
                                          <SeverityChip severity={c.severity} />
                                        </TableCell>
                                        <TableCell>{c.type ?? '--'}</TableCell>
                                        <TableCell>{c.workCenter ?? '--'}</TableCell>
                                        <TableCell>
                                          {c.date ? new Date(c.date).toLocaleDateString() : '--'}
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 300, whiteSpace: 'normal' }}>
                                          {c.description ?? '--'}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create MPS Record</DialogTitle>
        <DialogContent>
          <Box pt={1} display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Item Code"
              fullWidth
              value={form.itemCode}
              onChange={(e) => setForm({ ...form, itemCode: e.target.value })}
              required
            />
            <TextField
              label="Period (e.g. 2026-W01)"
              fullWidth
              value={form.period}
              onChange={(e) => setForm({ ...form, period: e.target.value })}
              required
            />
            <TextField
              label="Planned Quantity"
              type="number"
              fullWidth
              value={form.plannedQty}
              onChange={(e) => setForm({ ...form, plannedQty: e.target.value })}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={form.status}
                label="Status"
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!form.itemCode || !form.period || !form.plannedQty}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ─── Tab 2: MRP ───────────────────────────────────────────────────────────────
const MrpTab: React.FC = () => {
  const [plannedOrders, setPlannedOrders] = useState<PlannedOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Drill-down state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailOrder, setDetailOrder] = useState<PlannedOrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [releasing, setReleasing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/api/v1/planning/mrp/planned-orders');
      const list = data.data ?? data;
      setPlannedOrders(Array.isArray(list) ? list : []);
    } catch (e: any) {
      setError(e.message || 'Failed to load planned orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleViewDetail = async (id: string | number) => {
    setDetailOpen(true);
    setDetailOrder(null);
    setDetailLoading(true);
    try {
      const data = await apiFetch(`/api/v1/planning/mrp/planned-orders/${id}`);
      setDetailOrder(data.data ?? data);
    } catch (e: any) {
      setSnack({ open: true, message: `Failed to load order detail: ${e.message}`, severity: 'error' });
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRelease = async (id: string | number) => {
    setReleasing(true);
    try {
      await apiFetch(`/api/v1/planning/mrp/planned-orders/${id}/release`, { method: 'POST' });
      setSnack({ open: true, message: 'Order released successfully', severity: 'success' });
      setDetailOpen(false);
      load();
    } catch (e: any) {
      setSnack({ open: true, message: `Release failed: ${e.message}`, severity: 'error' });
    } finally {
      setReleasing(false);
    }
  };

  const handleRunMrp = async () => {
    setConfirmOpen(false);
    setRunning(true);
    try {
      await apiFetch('/api/v1/planning/mrp/calculate', { method: 'POST' });
      setSnack({ open: true, message: 'MRP calculation completed successfully', severity: 'success' });
      load();
    } catch (e: any) {
      setSnack({ open: true, message: `MRP calculation failed: ${e.message}`, severity: 'error' });
    } finally {
      setRunning(false);
    }
  };

  const handleOrderAction = async (id: string | number, action: 'confirm' | 'cancel') => {
    try {
      await apiFetch(`/api/v1/planning/mrp/planned-orders/${id}/${action}`, { method: 'PATCH' });
      setSnack({ open: true, message: `Order ${action}ed`, severity: 'success' });
      load();
    } catch (e: any) {
      setSnack({ open: true, message: `Failed to ${action} order: ${e.message}`, severity: 'error' });
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Material Requirements Planning</Typography>
        <Box display="flex" gap={1}>
          <Button size="small" startIcon={<Refresh />} onClick={load}>
            Refresh
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
            onClick={() => setConfirmOpen(true)}
            disabled={running}
          >
            {running ? 'Running MRP...' : 'Run MRP'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      <Typography variant="subtitle2" color="textSecondary" mb={1}>
        Planned Orders — click a row to view material requirements
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item Code</TableCell>
              <TableCell align="right">Required Qty</TableCell>
              <TableCell align="right">Available Qty</TableCell>
              <TableCell align="right">Shortage</TableCell>
              <TableCell>Planned Order Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((__, c) => (
                    <TableCell key={c}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : plannedOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary" py={2}>
                    No planned orders. Run MRP to generate requirements.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              plannedOrders.map((order) => {
                const id = order.id ?? Math.random();
                const shortage =
                  order.shortage ??
                  ((order.requiredQty ?? 0) - (order.availableQty ?? 0));
                return (
                  <TableRow
                    key={String(id)}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleViewDetail(id)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {order.itemCode ?? '--'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{order.requiredQty ?? '--'}</TableCell>
                    <TableCell align="right">{order.availableQty ?? '--'}</TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={shortage > 0 ? 'error.main' : 'success.main'}
                        fontWeight="medium"
                      >
                        {shortage}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {order.plannedOrderDate
                        ? new Date(order.plannedOrderDate).toLocaleDateString()
                        : '--'}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={order.status ?? 'PENDING'} />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          color="primary"
                          title="View detail"
                          onClick={() => handleViewDetail(id)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          onClick={() => handleOrderAction(id, 'confirm')}
                        >
                          Confirm
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleOrderAction(id, 'cancel')}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* MRP Confirm Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Run MRP Calculation</DialogTitle>
        <DialogContent>
          <Typography>
            This will calculate all material requirements based on the current MPS and inventory
            levels. This may take a few moments. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleRunMrp}>
            Run MRP
          </Button>
        </DialogActions>
      </Dialog>

      {/* Planned Order Detail Drawer */}
      <Drawer
        anchor="right"
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, p: 3 } }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Planned Order Detail</Typography>
          <IconButton onClick={() => setDetailOpen(false)}>
            <Close />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {detailLoading ? (
          <Box>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} variant="text" height={36} sx={{ mb: 1 }} />
            ))}
          </Box>
        ) : detailOrder ? (
          <Box>
            {/* Order Header */}
            <Grid container spacing={2} mb={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Item</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {detailOrder.itemCode ?? detailOrder.item ?? '--'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Quantity</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {detailOrder.quantity ?? detailOrder.requiredQty ?? '--'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Planned Start</Typography>
                <Typography variant="body2">
                  {detailOrder.plannedStart
                    ? new Date(detailOrder.plannedStart).toLocaleDateString()
                    : '--'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Planned End</Typography>
                <Typography variant="body2">
                  {detailOrder.plannedEnd
                    ? new Date(detailOrder.plannedEnd).toLocaleDateString()
                    : '--'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Status</Typography>
                <Box mt={0.5}>
                  <StatusChip status={detailOrder.status ?? 'PLANNED'} />
                </Box>
              </Grid>
              {detailOrder.source && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Source</Typography>
                  <Typography variant="body2">
                    {detailOrder.source === 'make' ? 'Make (Manufacture)' : 'Buy (Purchase)'}
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ mb: 2 }} />

            {/* Material Requirements */}
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
              Material Requirements
            </Typography>
            {Array.isArray(detailOrder.materialRequirements) && detailOrder.materialRequirements.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Material</TableCell>
                      <TableCell align="right">Required</TableCell>
                      <TableCell align="right">Available</TableCell>
                      <TableCell align="right">Shortage</TableCell>
                      <TableCell>Unit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailOrder.materialRequirements.map((mat, idx) => {
                      const shortage = mat.shortage ?? ((mat.requiredQty ?? 0) - (mat.availableQty ?? 0));
                      return (
                        <TableRow key={idx} sx={{ bgcolor: shortage > 0 ? 'error.light' : 'inherit' }}>
                          <TableCell>{mat.material ?? '--'}</TableCell>
                          <TableCell align="right">{mat.requiredQty ?? '--'}</TableCell>
                          <TableCell align="right">{mat.availableQty ?? '--'}</TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              color={shortage > 0 ? 'error.dark' : 'success.main'}
                              fontWeight="medium"
                            >
                              {shortage}
                            </Typography>
                          </TableCell>
                          <TableCell>{mat.unit ?? '--'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="textSecondary" mb={2}>
                No material requirements data available.
              </Typography>
            )}

            {/* Release button */}
            {(detailOrder.status ?? '').toLowerCase() === 'planned' && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={releasing}
                startIcon={releasing ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
                onClick={() => handleRelease(detailOrder.id!)}
              >
                {releasing ? 'Releasing...' : 'Release Order'}
              </Button>
            )}
          </Box>
        ) : null}
      </Drawer>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ─── Tab 3: CRP ───────────────────────────────────────────────────────────────
const CrpTab: React.FC = () => {
  const [rows, setRows] = useState<CrpRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [generating, setGenerating] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Drill-down state
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillRow, setDrillRow] = useState<CrpRow | null>(null);
  const [drillData, setDrillData] = useState<CrpUtilizationDetail[]>([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const load = useCallback(
    async (sd?: string, ed?: string) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (sd) params.set('startDate', sd);
        if (ed) params.set('endDate', ed);
        const qs = params.toString() ? `?${params.toString()}` : '';
        const data = await apiFetch(`/api/v1/planning/crp/capacity/analysis${qs}`);
        const list = data.data ?? data;
        setRows(Array.isArray(list) ? list : []);
      } catch (e: any) {
        setError(e.message || 'Failed to load CRP data');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const handleRowClick = async (row: CrpRow) => {
    const wcId = row.workCenterId ?? row.id;
    if (!wcId) return;
    setDrillRow(row);
    setDrillOpen(true);
    setDrillData([]);
    setDrillLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const data = await apiFetch(`/api/v1/planning/crp/work-centers/${wcId}/utilization${qs}`);
      const detail = data.data ?? data;
      // Backend may return an object with a breakdown array or the array directly
      const breakdown = Array.isArray(detail)
        ? detail
        : detail.breakdown ?? detail.weeklyBreakdown ?? detail.dailyBreakdown ?? [];
      setDrillData(breakdown);
    } catch (e: any) {
      setSnack({ open: true, message: `Failed to load utilization detail: ${e.message}`, severity: 'error' });
      setDrillOpen(false);
    } finally {
      setDrillLoading(false);
    }
  };

  const handleGenerateSchedule = async () => {
    setGenerating(true);
    try {
      const body: any = {};
      if (startDate) body.startDate = startDate;
      if (endDate) body.endDate = endDate;
      await apiFetch('/api/v1/planning/crp/schedule/generate', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setSnack({ open: true, message: 'Schedule generated successfully', severity: 'success' });
    } catch (e: any) {
      setSnack({ open: true, message: `Generate schedule failed: ${e.message}`, severity: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Capacity Requirements Planning</Typography>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <Button
            variant="outlined"
            onClick={() => load(startDate, endDate)}
            startIcon={<Refresh />}
          >
            Analyze
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
            onClick={handleGenerateSchedule}
            disabled={generating}
          >
            {generating ? 'Generating...' : 'Generate Schedule'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      <Typography variant="caption" color="textSecondary" mb={1} display="block">
        Click a work center row to see the weekly utilization drill-down.
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Work Center</TableCell>
              <TableCell align="right">Capacity Available (hrs)</TableCell>
              <TableCell align="right">Capacity Required (hrs)</TableCell>
              <TableCell>Utilization</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 5 }).map((__, c) => (
                    <TableCell key={c}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="textSecondary" py={2}>
                    No CRP data available. Select a date range and click Analyze.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const id = row.id ?? Math.random();
                const utilization = row.utilization ?? row.utilizationPercent ?? 0;
                return (
                  <TableRow
                    key={String(id)}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {row.workCenter ?? row.workCenterName ?? '--'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{row.capacityAvailable ?? row.availableHours ?? '--'}</TableCell>
                    <TableCell align="right">{row.capacityRequired ?? row.plannedHours ?? '--'}</TableCell>
                    <TableCell>
                      <UtilizationBar value={Number(utilization)} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          utilization > 100
                            ? 'OVERLOADED'
                            : utilization > 80
                            ? 'HIGH'
                            : 'NORMAL'
                        }
                        color={
                          utilization > 100
                            ? 'error'
                            : utilization > 80
                            ? 'warning'
                            : 'success'
                        }
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* CRP Drill-Down Dialog */}
      <Dialog
        open={drillOpen}
        onClose={() => setDrillOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Weekly Utilization — {drillRow?.workCenter ?? drillRow?.workCenterName ?? ''}
            </Typography>
            <IconButton onClick={() => setDrillOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {drillLoading ? (
            <Box>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={36} sx={{ mb: 0.5 }} />
              ))}
            </Box>
          ) : drillData.length === 0 ? (
            <Typography color="textSecondary" py={2} textAlign="center">
              No weekly breakdown data available for this work center.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Week / Date</TableCell>
                    <TableCell align="right">Planned Hours</TableCell>
                    <TableCell align="right">Available Hours</TableCell>
                    <TableCell>Utilization %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drillData.map((d, idx) => {
                    const pct = d.utilizationPercent ?? d.utilization ?? 0;
                    const pctNum = Number(pct);
                    const color =
                      pctNum > 100
                        ? 'error.main'
                        : pctNum >= 80
                        ? 'warning.main'
                        : 'success.main';
                    return (
                      <TableRow key={idx}>
                        <TableCell>{d.week ?? d.date ?? `Period ${idx + 1}`}</TableCell>
                        <TableCell align="right">{d.plannedHours ?? '--'}</TableCell>
                        <TableCell align="right">{d.availableHours ?? '--'}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(100, pctNum)}
                              color={pctNum > 100 ? 'error' : pctNum >= 80 ? 'warning' : 'success'}
                              sx={{ width: 80, height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="body2" color={color} fontWeight="medium">
                              {pctNum.toFixed(1)}%
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDrillOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ─── Gantt Chart (canvas-based) ───────────────────────────────────────────────
const GanttChart: React.FC<{
  schedules: ProductionSchedule[];
  viewStart: Date;
  viewEnd: Date;
}> = ({ schedules, viewStart, viewEnd }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);

  const ROW_HEIGHT = 36;
  const LABEL_WIDTH = 180;
  const HEADER_HEIGHT = 40;
  const PADDING = 4;

  const totalMs = viewEnd.getTime() - viewStart.getTime();

  // Generate day columns between viewStart and viewEnd
  const days: Date[] = [];
  const cur = new Date(viewStart);
  while (cur <= viewEnd) {
    days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const width = container.clientWidth || 800;
    const chartWidth = width - LABEL_WIDTH;
    const height = HEADER_HEIGHT + schedules.length * ROW_HEIGHT + PADDING * 2;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, width, height);

    // Background
    ctx.fillStyle = '#fafafa';
    ctx.fillRect(0, 0, width, height);

    // Header background
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(0, 0, width, HEADER_HEIGHT);

    // Label column header
    ctx.fillStyle = '#333';
    ctx.font = 'bold 12px Arial';
    ctx.fillText('Schedule / Work Center', 8, HEADER_HEIGHT / 2 + 5);

    // Day column headers
    const dayWidth = chartWidth / days.length;
    days.forEach((day, i) => {
      const x = LABEL_WIDTH + i * dayWidth;
      // Alternating column backgrounds
      if (i % 2 === 0) {
        ctx.fillStyle = '#f9f9f9';
        ctx.fillRect(x, HEADER_HEIGHT, dayWidth, height - HEADER_HEIGHT);
      }
      // Grid line
      ctx.strokeStyle = '#e0e0e0';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
      // Day label
      ctx.fillStyle = '#555';
      ctx.font = '10px Arial';
      const label = `${day.getMonth() + 1}/${day.getDate()}`;
      ctx.fillText(label, x + 3, HEADER_HEIGHT / 2 + 4);
    });

    // Right border of label column
    ctx.strokeStyle = '#bbb';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(LABEL_WIDTH, 0);
    ctx.lineTo(LABEL_WIDTH, height);
    ctx.stroke();

    // Draw rows
    schedules.forEach((sched, rowIdx) => {
      const y = HEADER_HEIGHT + rowIdx * ROW_HEIGHT;

      // Row background (alternating)
      ctx.fillStyle = rowIdx % 2 === 0 ? '#ffffff' : '#f5f5f5';
      ctx.fillRect(0, y, LABEL_WIDTH, ROW_HEIGHT);

      // Row label
      const label = sched.workCenter
        ? `${sched.workCenter}`
        : `#${sched.id ?? rowIdx + 1}`;
      const subLabel = sched.item ?? sched.itemCode ?? sched.workOrder ?? '';
      ctx.fillStyle = '#333';
      ctx.font = 'bold 11px Arial';
      ctx.fillText(label.slice(0, 22), 6, y + ROW_HEIGHT / 2 - 3);
      ctx.fillStyle = '#777';
      ctx.font = '10px Arial';
      ctx.fillText(subLabel.toString().slice(0, 24), 6, y + ROW_HEIGHT / 2 + 10);

      // Row separator
      ctx.strokeStyle = '#e8e8e8';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, y + ROW_HEIGHT);
      ctx.lineTo(width, y + ROW_HEIGHT);
      ctx.stroke();

      // Gantt bar
      if (sched.scheduledStart && sched.scheduledEnd) {
        const startMs = new Date(sched.scheduledStart).getTime();
        const endMs = new Date(sched.scheduledEnd).getTime();
        const barX =
          LABEL_WIDTH + ((startMs - viewStart.getTime()) / totalMs) * chartWidth;
        const barW = ((endMs - startMs) / totalMs) * chartWidth;

        const status = (sched.status ?? 'DRAFT').toUpperCase();
        const barColor = GANTT_COLORS[status] ?? '#9e9e9e';

        // Bar
        ctx.fillStyle = barColor;
        ctx.beginPath();
        const bx = Math.max(LABEL_WIDTH, barX);
        const bw = Math.max(4, barW - Math.max(0, LABEL_WIDTH - barX));
        const by = y + PADDING + 2;
        const bh = ROW_HEIGHT - PADDING * 2 - 4;
        ctx.roundRect(bx, by, bw, bh, 4);
        ctx.fill();

        // Bar label (status)
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 10px Arial';
        if (bw > 30) {
          ctx.fillText(status, bx + 6, by + bh / 2 + 4);
        }
      }
    });
  }, [schedules, viewStart, viewEnd, days, totalMs]);

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const rowIdx = Math.floor((my - HEADER_HEIGHT) / ROW_HEIGHT);
    if (rowIdx >= 0 && rowIdx < schedules.length) {
      const s = schedules[rowIdx];
      const start = s.scheduledStart ? new Date(s.scheduledStart).toLocaleDateString() : 'N/A';
      const end = s.scheduledEnd ? new Date(s.scheduledEnd).toLocaleDateString() : 'N/A';
      setTooltip({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        text: `${s.workCenter ?? 'Schedule'} | ${s.status ?? ''} | ${start} – ${end}`,
      });
    } else {
      setTooltip(null);
    }
  };

  const canvasHeight = HEADER_HEIGHT + schedules.length * ROW_HEIGHT + PADDING * 2;

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%', overflowX: 'auto' }}>
      <canvas
        ref={canvasRef}
        height={canvasHeight}
        style={{ display: 'block', width: '100%', cursor: 'crosshair' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
      />
      {tooltip && (
        <Box
          sx={{
            position: 'absolute',
            left: tooltip.x + 12,
            top: tooltip.y - 8,
            bgcolor: 'rgba(0,0,0,0.75)',
            color: '#fff',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            fontSize: 12,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            zIndex: 10,
          }}
        >
          {tooltip.text}
        </Box>
      )}
    </Box>
  );
};

// ─── Tab 4: Schedule (Gantt) ──────────────────────────────────────────────────
const ScheduleTab: React.FC = () => {
  const [schedules, setSchedules] = useState<ProductionSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');
  const [viewStartStr, setViewStartStr] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay()); // Sunday of current week
    return d.toISOString().split('T')[0];
  });
  const [workCenterFilter, setWorkCenterFilter] = useState<string>('');

  // Compute view window dates
  const viewStart = new Date(viewStartStr + 'T00:00:00');
  const viewEnd = new Date(viewStart);
  if (viewMode === 'week') {
    viewEnd.setDate(viewEnd.getDate() + 6);
  } else {
    viewEnd.setDate(viewEnd.getDate() + 29);
  }
  viewEnd.setHours(23, 59, 59, 999);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/api/v1/planning/crp/schedules');
      const list = data.data ?? data;
      setSchedules(Array.isArray(list) ? list : []);
    } catch (e: any) {
      setError(e.message || 'Failed to load production schedules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Derive unique work centers for filter
  const workCenters: string[] = Array.from(
    new Set(schedules.map((s) => s.workCenter ?? '').filter(Boolean))
  );

  const filteredSchedules = schedules.filter((s) => {
    if (workCenterFilter && s.workCenter !== workCenterFilter) return false;
    return true;
  });

  // Legend items
  const legend = [
    { label: 'Draft', color: GANTT_COLORS.DRAFT },
    { label: 'Active', color: GANTT_COLORS.ACTIVE },
    { label: 'Completed', color: GANTT_COLORS.COMPLETED },
    { label: 'Cancelled', color: GANTT_COLORS.CANCELLED },
  ];

  const navigate = (dir: number) => {
    const d = new Date(viewStartStr + 'T00:00:00');
    if (viewMode === 'week') {
      d.setDate(d.getDate() + dir * 7);
    } else {
      d.setDate(d.getDate() + dir * 30);
    }
    setViewStartStr(d.toISOString().split('T')[0]);
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h6">Production Schedule</Typography>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          {/* Work center filter */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Work Center</InputLabel>
            <Select
              value={workCenterFilter}
              label="Work Center"
              onChange={(e) => setWorkCenterFilter(e.target.value as string)}
            >
              <MenuItem value="">All</MenuItem>
              {workCenters.map((wc) => (
                <MenuItem key={wc} value={wc}>
                  {wc}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Week / Month toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => { if (v) setViewMode(v); }}
            size="small"
          >
            <ToggleButton value="week">
              <CalendarViewWeek fontSize="small" sx={{ mr: 0.5 }} />
              Week
            </ToggleButton>
            <ToggleButton value="month">
              <CalendarViewMonth fontSize="small" sx={{ mr: 0.5 }} />
              Month
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Date navigation */}
          <Button size="small" variant="outlined" onClick={() => navigate(-1)}>
            &lt; Prev
          </Button>
          <Typography variant="body2" sx={{ minWidth: 160, textAlign: 'center' }}>
            {viewStart.toLocaleDateString()} – {viewEnd.toLocaleDateString()}
          </Typography>
          <Button size="small" variant="outlined" onClick={() => navigate(1)}>
            Next &gt;
          </Button>

          <Button size="small" startIcon={<Refresh />} onClick={load}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Legend */}
      <Box display="flex" gap={2} mb={1} flexWrap="wrap">
        {legend.map((l) => (
          <Box key={l.label} display="flex" alignItems="center" gap={0.5}>
            <Box sx={{ width: 16, height: 16, bgcolor: l.color, borderRadius: 0.5 }} />
            <Typography variant="caption">{l.label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Gantt chart */}
      <Paper sx={{ mb: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box p={3}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={36} sx={{ mb: 0.5 }} />
            ))}
          </Box>
        ) : filteredSchedules.length === 0 ? (
          <Box p={3} textAlign="center">
            <Typography color="textSecondary">
              No schedules found for the selected filters.
            </Typography>
          </Box>
        ) : (
          <GanttChart
            schedules={filteredSchedules}
            viewStart={viewStart}
            viewEnd={viewEnd}
          />
        )}
      </Paper>

      {/* Schedule table */}
      <Typography variant="subtitle2" color="textSecondary" mb={1}>
        Schedule Details
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Work Order</TableCell>
              <TableCell>Item</TableCell>
              <TableCell>Work Center</TableCell>
              <TableCell>Scheduled Start</TableCell>
              <TableCell>Scheduled End</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((__, c) => (
                    <TableCell key={c}><Skeleton variant="text" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : filteredSchedules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary" py={2}>
                    No schedules to display.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredSchedules.map((s) => {
                const id = s.id ?? Math.random();
                return (
                  <TableRow key={String(id)} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {String(s.id ?? '--').slice(0, 8)}
                      </Typography>
                    </TableCell>
                    <TableCell>{s.workOrder ?? s.workOrderId ?? '--'}</TableCell>
                    <TableCell>{s.item ?? s.itemCode ?? '--'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {s.workCenter ?? '--'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {s.scheduledStart
                        ? new Date(s.scheduledStart).toLocaleString()
                        : '--'}
                    </TableCell>
                    <TableCell>
                      {s.scheduledEnd
                        ? new Date(s.scheduledEnd).toLocaleString()
                        : '--'}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={s.status ?? 'DRAFT'} />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ─── Main Planning component ──────────────────────────────────────────────────
const Planning: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Manufacturing Planning
        </Typography>
      </Box>

      {/* Summary KPI cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    MPS (Master Schedule)
                  </Typography>
                  <Typography variant="body1" color="primary.main">
                    Real-time data
                  </Typography>
                </Box>
                <Schedule sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    MRP Calculation
                  </Typography>
                  <Typography variant="body1" color="success.main">
                    On-demand
                  </Typography>
                </Box>
                <Inventory sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Capacity Planning
                  </Typography>
                  <Typography variant="body1" color="warning.main">
                    CRP Analysis
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Planned Orders
                  </Typography>
                  <Typography variant="body1" color="info.main">
                    Live feed
                  </Typography>
                </Box>
                <Assignment sx={{ fontSize: 40, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Master Production Schedule (MPS)" />
          <Tab label="Material Requirements Planning (MRP)" />
          <Tab label="Capacity Planning (CRP)" />
          <Tab label="Production Schedule" icon={<Timeline />} iconPosition="start" />
        </Tabs>
      </Paper>

      {selectedTab === 0 && <MpsTab />}
      {selectedTab === 1 && <MrpTab />}
      {selectedTab === 2 && <CrpTab />}
      {selectedTab === 3 && <ScheduleTab />}
    </Box>
  );
};

export default Planning;
